import React, { useEffect, useState } from 'react';
import styles from './NotificationSettings.module.css';

// Pretty-print E.164 phone numbers, e.g. "+18332338790" -> "(833) 233-8790".
// Used to surface the active Twilio sender in the SMS column header so admins
// can confirm at a glance that messaging is live and which number parents see.
function formatPhone(e164) {
  if (!e164) return '';
  const digits = String(e164).replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return e164;
}

// Friendly labels + descriptions for each event the engine knows about.
const EVENT_META = {
  creditLoaded:        { label: 'Credit Loaded',         desc: 'Receipt sent when an account is topped up' },
  sessionCharged:      { label: 'Session Charged',       desc: 'Receipt for an admin-charged training session' },
  lowBalance:          { label: 'Low Balance',           desc: 'Manual nudge from the admin Account section' },
  accountStatement:    { label: 'Account Statement',     desc: 'Balance + recent activity summary' },
  assessment:          { label: 'Athlete Assessment',    desc: 'Send-to-Parent on an athlete assessment' },
  parentInvite:        { label: 'Parent Invite / Reset', desc: 'Setup link to the parent portal (or password reset)' },
  bookingConfirmation: { label: 'Booking Confirmation',  desc: 'Sent when a session is booked through Square' },
  mediaPosted:         { label: 'Media Posted',          desc: 'Nudge to parents when new photos/videos are added' },
};

export default function NotificationSettings({ API, authHeader, showToast }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [twilio, setTwilio] = useState(null);  // { configured, fromNumber }

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/config/notifications`, { headers: { ...authHeader() } });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || 'Failed to load notification settings');
        setData(json);
      } catch (e) {
        setError(e.message);
      }
    })();
    // Separately poll the Twilio status -- failure is non-fatal; the UI just
    // falls back to "pending Twilio".
    (async () => {
      try {
        const res = await fetch(`${API}/integrations/twilio/status`, { headers: { ...authHeader() } });
        if (res.ok) setTwilio(await res.json());
      } catch { /* ignore */ }
    })();
  }, [API]);

  const toggle = (channel, event) => {
    setData(prev => ({
      ...prev,
      toggles: {
        ...prev.toggles,
        [channel]: { ...prev.toggles[channel], [event]: !prev.toggles[channel][event] },
      },
    }));
    setDirty(true);
  };

  const setRow = (event, value) => {
    setData(prev => ({
      ...prev,
      toggles: {
        email: { ...prev.toggles.email, [event]: value },
        sms:   { ...prev.toggles.sms,   [event]: value },
      },
    }));
    setDirty(true);
  };

  const setColumn = (channel, value) => {
    setData(prev => ({
      ...prev,
      toggles: {
        ...prev.toggles,
        [channel]: Object.fromEntries(prev.events.map(e => [e, value])),
      },
    }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/config/notifications`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ toggles: data.toggles }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Save failed');
      setDirty(false);
      showToast?.('Notification settings saved');
    } catch (e) {
      showToast?.(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (error) return <div className={styles.errorCard}>Couldn't load notification settings: {error}</div>;
  if (!data) return <div className={styles.loadingCard}>Loading…</div>;

  return (
    <div className={styles.page}>
      <div className={styles.headerCard}>
        <h2 className={styles.title}>Notifications</h2>
        <p className={styles.lead}>
          Control which events send to parents and through which channel.
          {twilio?.configured ? (
            <>
              {' '}SMS goes out from <strong>{formatPhone(twilio.fromNumber)}</strong> — every
              parent sees all NGS messages as one thread in their Messages app.
            </>
          ) : (
            <> SMS will go live once Twilio is wired up — the toggles are saved either way.</>
          )}
        </p>
      </div>

      <div className={styles.gridCard}>
        <table className={styles.grid}>
          <thead>
            <tr>
              <th className={styles.eventCol}>Event</th>
              <th className={styles.channelCol}>
                <div className={styles.channelHeader}>
                  <span>Email</span>
                  <div className={styles.channelToggleRow}>
                    <button className={styles.tinyBtn} onClick={() => setColumn('email', true)}>All on</button>
                    <button className={styles.tinyBtn} onClick={() => setColumn('email', false)}>All off</button>
                  </div>
                </div>
              </th>
              <th className={styles.channelCol}>
                <div className={styles.channelHeader}>
                  <span>
                    SMS{' '}
                    {twilio?.configured ? (
                      <span className={styles.activeTag} title={twilio.fromNumber}>
                        active · {formatPhone(twilio.fromNumber)}
                      </span>
                    ) : (
                      <span className={styles.pendingTag}>pending Twilio</span>
                    )}
                  </span>
                  <div className={styles.channelToggleRow}>
                    <button className={styles.tinyBtn} onClick={() => setColumn('sms', true)}>All on</button>
                    <button className={styles.tinyBtn} onClick={() => setColumn('sms', false)}>All off</button>
                  </div>
                </div>
              </th>
              <th className={styles.rowActionsCol}></th>
            </tr>
          </thead>
          <tbody>
            {data.events.map(event => {
              const meta = EVENT_META[event] || { label: event, desc: '' };
              const emailOn = data.toggles.email[event];
              const smsOn = data.toggles.sms[event];
              return (
                <tr key={event}>
                  <td className={styles.eventCell}>
                    <div className={styles.eventLabel}>{meta.label}</div>
                    {meta.desc && <div className={styles.eventDesc}>{meta.desc}</div>}
                  </td>
                  <td className={styles.toggleCell}>
                    <Toggle on={emailOn} onClick={() => toggle('email', event)} />
                  </td>
                  <td className={styles.toggleCell}>
                    <Toggle on={smsOn} onClick={() => toggle('sms', event)} />
                  </td>
                  <td className={styles.rowActionsCell}>
                    <button className={styles.rowAction} onClick={() => setRow(event, true)}>Both on</button>
                    <button className={styles.rowAction} onClick={() => setRow(event, false)}>Both off</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className={styles.actions}>
          <span className={styles.dirtyHint}>{dirty ? 'Unsaved changes' : 'All changes saved'}</span>
          <button className={styles.saveBtn} disabled={!dirty || saving} onClick={save}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Toggle({ on, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${styles.toggle} ${on ? styles.toggleOn : ''}`}
      role="switch"
      aria-checked={on}
    >
      <span className={styles.toggleKnob} />
    </button>
  );
}
